import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTranscript } from './useTranscript';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const AUDIO_TRANSCRIPT_DURATION_DIFFERENCE_THRESHOLD = 10; // in seconds
export const AUDIO_ERROR_TYPE = {
  INVALID_URL: 'invalid-url',
  AUDIO_DURATION: 'audio-duration',
};

export const useEpisodeUrl = (id?: string) => {
  const { t } = useTranslation();
  const { selectedEpisode } = useInsightsContext();
  const { getEpisodeById } = useEpisodeDetailsData();
  const { transcriptDuration } = useTranscript();

  const episodeId = useMemo(() => id || selectedEpisode, [selectedEpisode, id]);
  const episode = getEpisodeById(episodeId);

  const [url, setUrl] = useState<string | undefined | null>(null);
  const [isValidOriginalUrlUsed, setIsValidOriginalUrlUsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [audioDuration, setAudioDuration] = useState<number | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);

  const handleHostedUrlSuccess = (duration: number | undefined) => {
    setUrl(episode?.hosted_url);
    setAudioDuration(duration);
    setIsLoading(false);
  };

  const handleHostedUrlError = (originalUrl: string) => {
    const audio = new Audio(originalUrl);

    audio.addEventListener('loadedmetadata', () => handleOriginalUrlSuccess(audio?.duration));
    audio.addEventListener('error', () => handleOriginalUrlError(originalUrl));
  };

  const handleOriginalUrlSuccess = (duration: number | undefined) => {
    setUrl(episode?.original_url);
    setAudioDuration(duration);
    setIsValidOriginalUrlUsed(true);
    setIsLoading(false);
  };

  const handleOriginalUrlError = (originalUrl: string) => {
    setUrl(originalUrl);
    setIsLoading(false);
    setError(
      new Error(t('insights.episode_details_drawer.player.invalid_url_error_message'), {
        cause: AUDIO_ERROR_TYPE.INVALID_URL,
      })
    );
  };

  useEffect(() => {
    let audio: HTMLAudioElement | null;

    if (episode?.original_url && !episode?.hosted_url) {
      audio = new Audio(episode.original_url);
      audio.addEventListener('loadedmetadata', () => handleOriginalUrlSuccess(audio?.duration));
      audio.addEventListener('error', () => handleOriginalUrlError(episode.original_url));

      return;
    }

    if (episode?.hosted_url) {
      audio = new Audio(episode.hosted_url);
      audio.addEventListener('loadedmetadata', () => handleHostedUrlSuccess(audio?.duration));
      audio.addEventListener('error', () => handleHostedUrlError(episode.original_url));
    }

    return () => {
      if (episode?.original_url && !episode?.hosted_url) {
        audio?.removeEventListener('loadedmetadata', () =>
          handleOriginalUrlSuccess(audio?.duration)
        );
        audio?.removeEventListener('error', () => handleOriginalUrlError(episode.original_url));
      }

      if (episode?.hosted_url) {
        audio?.removeEventListener('loadedmetadata', () => handleHostedUrlSuccess(audio?.duration));
        audio?.removeEventListener('error', () => handleHostedUrlError(episode.original_url));
      }
    };
  }, [episode?.hosted_url, episode?.original_url]);

  // only track audio duration difference if the original url is used, and set the error if the difference is greater than the threshold
  useEffect(() => {
    if (url && transcriptDuration && audioDuration && isValidOriginalUrlUsed) {
      if (
        Math.abs(Number(transcriptDuration) - audioDuration) >
        AUDIO_TRANSCRIPT_DURATION_DIFFERENCE_THRESHOLD
      ) {
        setError(
          new Error(
            t('insights.episode_details_drawer.player.audio_duration_mismatch_error_message'),
            { cause: AUDIO_ERROR_TYPE.AUDIO_DURATION }
          )
        );
      }
    }
  }, [transcriptDuration, audioDuration, isValidOriginalUrlUsed]);

  return { url, isLoading, error };
};
